import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEvaluatorAssignmentDetail,
  getEvaluatorEvaluation,
  listEvaluatorAssignments,
  saveEvaluatorDraft,
  submitEvaluatorEvaluation,
  type SaveDraftPayload,
  type SubmitEvaluationPayload,
} from '@/api/evaluator-portal';

export const evaluatorPortalKeys = {
  root: (token: string) => ['evaluator-portal', token] as const,
  assignments: (token: string) => [...evaluatorPortalKeys.root(token), 'assignments'] as const,
  assignment: (token: string, assignmentId: string) => [...evaluatorPortalKeys.root(token), 'assignment', assignmentId] as const,
  evaluation: (token: string, assignmentId: string) => [...evaluatorPortalKeys.root(token), 'evaluation', assignmentId] as const,
};

export function useEvaluatorPortalAssignments(token: string) {
  return useQuery({
    queryKey: evaluatorPortalKeys.assignments(token),
    queryFn: () => listEvaluatorAssignments(token),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

export function useEvaluatorPortalAssignmentDetail(token: string, assignmentId: string) {
  return useQuery({
    queryKey: evaluatorPortalKeys.assignment(token, assignmentId),
    queryFn: () => getEvaluatorAssignmentDetail(token, assignmentId),
    enabled: Boolean(token && assignmentId),
    staleTime: 30_000,
  });
}

export function useEvaluatorPortalEvaluation(token: string, assignmentId: string) {
  return useQuery({
    queryKey: evaluatorPortalKeys.evaluation(token, assignmentId),
    queryFn: () => getEvaluatorEvaluation(token, assignmentId),
    enabled: Boolean(token && assignmentId),
    staleTime: 5_000,
  });
}

export function useSaveEvaluatorDraft(token: string, assignmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveDraftPayload) => saveEvaluatorDraft(token, assignmentId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.assignments(token) }),
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.assignment(token, assignmentId) }),
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.evaluation(token, assignmentId) }),
      ]);
    },
  });
}

export function useSubmitEvaluatorEvaluation(token: string, assignmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SubmitEvaluationPayload) => submitEvaluatorEvaluation(token, assignmentId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.assignments(token) }),
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.assignment(token, assignmentId) }),
        queryClient.invalidateQueries({ queryKey: evaluatorPortalKeys.evaluation(token, assignmentId) }),
      ]);
    },
  });
}
